import { Calendar, ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import type { NewsItem } from '../types';
import { Helmet } from 'react-helmet-async';

export function SingleNews() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('news_english')
          .select('*')
          .eq('slug', slug)
          .single();

        if (error) throw error;
        setNews(data);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Could not load the article');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-lg shadow-lg p-6 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="h-96 bg-gray-200 rounded mb-6"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <p className="text-gray-600">{error || 'Article not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  const formattedDate = new Date(news.created_at).toLocaleDateString('no-NO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <>
      <Helmet>
        <title>{news.title} | Aker Stadion</title>
        <meta 
          name="description" 
          content={news.content.substring(0, 155) + '...'}
        />
      </Helmet>
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#2173B9] hover:text-[#1b5c94] mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {news.imageUrl && (
              <img 
                src={news.imageUrl} 
                alt={news.title}
                className="w-full h-[400px] object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/images/f-image-molde.png';
                }}
              />
            )}
            <div className="p-8">
              <h1 className="text-3xl font-bold mb-4">{news.title}</h1>
              <div className="flex items-center gap-2 mb-8 text-gray-600">
                <Calendar className="w-5 h-5" />
                <span>{formattedDate}</span>
              </div>
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: news.content }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
